import { type FC, useState } from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Box,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
} from "@mui/material";
import { useFormik, FormikProvider, Form } from "formik";
import * as Yup from "yup";
import { aidRequestFormRepo, categoryRepo } from "@repositories";
import { provide } from "../../helpers/wrap-and-provide";
import FormProvider from "../../helpers/forms/FormProvider";
import { FormTextField } from "../../components/form-components/form-text-field/FormTextField";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { type Team } from "@entities";
import { TeamsSelectDialog } from "../../components/teams-select";
import { useAlert } from "../../contexts/AlertProvider";

interface AddAidRequestFormDialogProps {
    open: boolean;
    onClose: () => void;
    initialValues?: {
        title: string;
        description: string;
        categories: string[];
        notes?: string;
        id: string;
        team?: Team;
    };
}

const validationSchema = Yup.object({
    title: Yup.string().required("כותרת היא שדה חובה"),
    description: Yup.string().required("תיאור הוא שדה חובה"),
    categories: Yup.array()
        .of(Yup.string().required("קטגוריה היא שדה חובה"))
        .required("קטגוריות הן שדה חובה"),
    team: Yup.object().optional(),
});

const AddAidRequestFormDialog: FC<AddAidRequestFormDialogProps> = ({ open, onClose, initialValues }) => {
    const { currOrganization, getCurrOrganization } = useOrganization();
    const [isTeamsSelectOpen, setIsTeamsSelectOpen] = useState<boolean>(false);
    const { setAlertText, setSeverity } = useAlert();

    const formik = useFormik({
        initialValues: {
            title: "",
            description: "",
            categories: [],
            newCategory: "",
            notes: "",
            team: undefined,
            ...initialValues, // Merge with initialValues prop
        },
        validationSchema,
        onSubmit: async (values) => {
            if (initialValues?.id) {
                const newForm = await aidRequestFormRepo.update(initialValues.id, {
                    title: values.title,
                    description: values.description,
                    organization: currOrganization,
                    notes: values.notes,
                    hasTime: true,
                    team: values.team,
                });

                const existingCategoryNames = initialValues.categories || [];
                const categoriesToAdd = values.categories.filter(
                    (category) => !existingCategoryNames.includes(category),
                );
                const categoriesToRemove = existingCategoryNames.filter(
                    (cat) => !values.categories.includes(cat),
                );

                const categories = categoriesToAdd.map((categoryName) =>
                ({
                    name: categoryName,
                    organization: currOrganization,
                    aidRequestForm: newForm,
                }),
                );
                await categoryRepo.insert(categories);

                await categoryRepo.deleteMany({
                    where: {
                        name: categoriesToRemove,
                        organization: currOrganization,
                        aidRequestForm: newForm,
                    }
                });
            } else {
                const newForm = await aidRequestFormRepo.insert({
                    title: values.title,
                    description: values.description,
                    organization: currOrganization,
                    notes: values.notes,
                    hasTime: true,
                    team: values.team,
                });
                const categories = values.categories.map((categoryName) => ({
                    name: categoryName,
                    organization: currOrganization,
                    aidRequestForm: newForm,
                }));
                await categoryRepo.insert(categories);
            }
            onClose();
            getCurrOrganization();
            setAlertText(initialValues?.id ? "הטופס עודכן בהצלחה" : "הטופס נוסף בהצלחה");
            setSeverity("success");
        },
    });

    const addCategory = (category: string) => {
        formik.setFieldValue("categories", [...formik.values.categories, category]);
    };

    return (<>
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', textAlign: 'center' }}>
                {initialValues?.id ? "עריכת" : "הוספת"} טופס סיוע
            </DialogTitle>
            <DialogContent sx={{ padding: '24px', backgroundColor: '#fafafa' }}>
                <FormikProvider value={formik}>
                    <Form onSubmit={formik.handleSubmit}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <FormTextField
                                name="title"
                                label="שם הטופס"
                                fullWidth
                            />
                            <FormTextField
                                name="description"
                                label="תיאור"
                                multiline
                                rows={6}
                                fullWidth
                            />
                            <FormTextField
                                name="notes"
                                label="הערות"
                                fullWidth
                            />
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '16px',
                                padding: '8px',
                                backgroundColor: '#f9f9f9',
                                borderRadius: '8px',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333' }}>
                                    {formik.values.team?.name || "לא נבחרה פרויקט"}
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => setIsTeamsSelectOpen(true)}
                                    sx={{ textTransform: 'none', fontWeight: 'bold', padding: '0.5rem 1rem', borderRadius: "0.5rem" }}
                                >
                                    הוספת פרויקט
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#333' }}>
                                    קטגוריות
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                                    <FormTextField
                                        name="newCategory"
                                        label="הוספת קטגוריה חדשה"
                                        fullWidth
                                        sx={{ backgroundColor: '#fff', borderRadius: '4px' }}
                                    />
                                    <Button
                                        onClick={() => {
                                            const category = formik.values.newCategory?.trim();
                                            if (category) {
                                                addCategory(category);
                                                formik.setFieldValue("newCategory", "");
                                            }
                                        }}
                                        color="primary"
                                        variant="contained"
                                        sx={{ textTransform: 'none', fontWeight: 'bold', paddingBlock: '0.7rem', borderRadius: "1rem" }}
                                    >
                                        הוספה
                                    </Button>
                                </Box>
                                {formik.values.categories.length > 0 && <List sx={{
                                    backgroundColor: '#f9f9f9',
                                    borderRadius: '8px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                    padding: '8px',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, 1fr)',
                                    gap: '8px',
                                }}>
                                    {formik.values.categories.map((category, index) => (
                                        <ListItem
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '8px 16px',
                                                backgroundColor: '#fff',
                                                borderRadius: '4px',
                                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                            }}
                                        >
                                            <ListItemText primary={category} sx={{ color: '#555' }} />
                                            <Button
                                                onClick={() => {
                                                    const updatedCategories = formik.values.categories.filter((_, i) => i !== index);
                                                    formik.setFieldValue("categories", updatedCategories);
                                                }}
                                                color="secondary"
                                                variant="text"
                                                sx={{ textTransform: 'none', fontWeight: 'bold', color: '#d32f2f' }}
                                            >
                                                הסר
                                            </Button>
                                        </ListItem>
                                    ))}
                                </List>}
                            </Box>
                        </Box>
                    </Form>
                </FormikProvider>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', padding: '16px', backgroundColor: '#f5f5f5' }}>
                <Button onClick={onClose} variant="outlined" color="secondary">
                    ביטול
                </Button>
                <Button onClick={() => formik.handleSubmit()} color="primary" variant="contained">
                    שמירה
                </Button>
            </DialogActions>
        </Dialog>
        <TeamsSelectDialog
            onClose={() => setIsTeamsSelectOpen(false)}
            open={isTeamsSelectOpen}
            isOne
            returnFullTeam
            onSave={(team) => {
                formik.setFieldValue("team", team?.[1] || team?.[0] || undefined);
                setIsTeamsSelectOpen(false);
            }}
            value={formik.values.team ? [formik.values.team] : []}
        />
    </>
    );
};
export default provide([
    FormProvider,
    {
        initialValues: {
            title: "",
            description: "",
            categories: [],
            team: undefined
        },
        validationSchema,
        onSubmit: () => { },
    },
])(AddAidRequestFormDialog);
